import { gql } from '@apollo/client';
import { Table, useSearchField } from '@everlutionsk/ui';
import { CardContent, TableTitle } from '@everlutionsk/ui-admin';
import { useOffsetPaginatedQuery } from '@everlutionsk/ui-apollo';
import { Link } from '@everlutionsk/ui-router';
import { Typography } from '@mui/material';
import { formatDistance, isAfter } from 'date-fns';
import { isEmpty } from 'ramda';
import React from 'react';
import { TableRowNavigation } from '../../components/TableRowNavigation';
import { TableWrapper } from '../../components/TableWrapper';
import { TimeAgo } from '../../components/TimeAgo';
import { Licence, UserStatus } from '../../graphql/types';
import { prettifyStatus } from '../../prettifyData';
import { routing } from '../../Routes';

export function TrialManagerList() {
  const [term, searchInput] = useSearchField('trialManagerList.term');

  const { data, pagination } = useOffsetPaginatedQuery(query, {
    variables: ({ limit, offset }) => ({
      input: {
        pagination: { limit, offset },
        licence: Licence.trial,
        status: [UserStatus.activated, UserStatus.invited, UserStatus.suspended],
        term
      }
    })
  });

  return (
    <>
      <CardContent>{searchInput}</CardContent>
      <CardContent>
        <TableTitle
          total={data?.managerMany.total}
          title="Trial PR users"
          totalNumberDescription="results matching your search"
        />
        <TableWrapper>
          <Table
            pagination={pagination}
            items={data?.managerMany.items}
            columns={[
              {
                label: 'Name',
                render: ({ fullName, id }) => (
                  <Link to={routing.managers.detail(id)}>{fullName}</Link>
                )
              },
              {
                label: 'Company',
                render: ({ company }) => (company != null && !isEmpty(company) ? company : '-')
              },
              {
                label: 'Country',
                render: ({ country }) => (country != null && !isEmpty(country) ? country : '-')
              },
              {
                label: 'Status',
                render: ({ status }) => prettifyStatus(status)
              },
              {
                label: 'Trial',
                render: ({ licenceExpiredAt }) =>
                  licenceExpiredAt ? <LicenceEndsIn iso={licenceExpiredAt} /> : 'Never'
              },
              {
                label: 'Last login',
                render: ({ lastActiveAt }) =>
                  lastActiveAt ? <TimeAgo iso={lastActiveAt} /> : 'Never'
              },
              {
                label: 'Cognito ID',
                render: ({ cognitoId }) => cognitoId ?? '-'
              },
              {
                align: 'right',
                label: '',
                render: ({ id }) => <TableRowNavigation to={routing.managers.detail(id)} />
              }
            ]}
          />
        </TableWrapper>
      </CardContent>
    </>
  );
}

const query = gql<ManagerTrialManyGQL>`
  query ManagerTrialMany($input: ManagerManyInput!) {
    managerMany(input: $input) {
      total
      items {
        id
        fullName
        company
        country
        status
        licence
        lastActiveAt
        requestedAt
        licenceExpiredAt
        cognitoId
      }
    }
  }
`;

export function LicenceEndsIn({ iso }: { iso: string }) {
  if (iso == null) return null;
  const now = new Date();
  const endsAt = new Date(iso);

  if (isAfter(now, endsAt))
    return (
      <Typography color="error" variant="body2">
        Ended
      </Typography>
    );

  const timeAgo = formatDistance(new Date(iso), new Date(), { addSuffix: true });
  return <>Ends {timeAgo}</>;
}
